import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { ArrowLeft, Loader2, ExternalLink } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { formatDateFull } from '../../utils/timeFormat';

const fmt = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ISSUE_LABELS = {
  no_adjustment_ledger: 'Approved correction was never applied',
  booking_amount_not_updated: 'Booking amount not updated',
  rate_not_applied: 'Approved rate not applied',
};

const RESOLUTIONS = [
  { value: 'approved', label: 'Approve' },
  { value: 'partially_approved', label: 'Partially approve' },
  { value: 'adjusted', label: 'Adjust hours / amount' },
  { value: 'request_info', label: 'Request more information' },
  { value: 'rejected', label: 'Reject' },
];

export default function SettlementChallenges() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('open');
  const [data, setData] = useState({ challenges: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [resolution, setResolution] = useState('');
  const [adjHours, setAdjHours] = useState('');
  const [adjRate, setAdjRate] = useState('');
  const [adjAmount, setAdjAmount] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [talentResponse, setTalentResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [payoutDate, setPayoutDate] = useState('');
  const [payoutNote, setPayoutNote] = useState('');
  const [savingPayout, setSavingPayout] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [reconciling, setReconciling] = useState('');

  const runAudit = async () => {
    setAuditing(true); setAuditOpen(true);
    try {
      const res = await api.get('/admin/settlement-challenge-audit');
      setAudit(res.data);
    } catch {
      toast.error('Could not run the financial audit.');
    } finally { setAuditing(false); }
  };

  const reconcile = async (challengeId) => {
    setReconciling(challengeId);
    try {
      const res = await api.post(`/admin/settlement-challenges/${challengeId}/reconcile`);
      toast.success(typeof res.data?.message === 'string' ? res.data.message : 'Reconciled.');
      await runAudit();
      load(tab);
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Could not reconcile.');
    } finally { setReconciling(''); }
  };

  const load = async (which = tab) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/settlement-challenges?status=${which}`);
      setData(res.data);
    } catch (e) {
      toast.error('Could not load settlement challenges.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const openDetail = (c) => {
    setActive(c);
    setResolution('');
    setAdjHours('');
    setAdjRate('');
    setAdjAmount('');
    setInternalNote('');
    setTalentResponse('');
    setPayoutDate(c.revised_payout_date || c.live_payout_date || '');
    setPayoutNote('');
  };

  const savePayoutDate = async () => {
    if (!payoutDate) { toast.error('Pick an updated payout date.'); return; }
    setSavingPayout(true);
    try {
      const res = await api.post(`/admin/settlement-challenges/${active.id}/payout-date`, {
        revised_payout_date: payoutDate,
        ...(payoutNote.trim() ? { note: payoutNote.trim() } : {}),
      });
      toast.success(`Updated payout date set — ${active.talent_name} has been notified.`);
      setActive((a) => a ? { ...a, revised_payout_date: res.data?.revised_payout_date || payoutDate, payout_update_requested: false } : a);
      load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not set the payout date.');
    } finally {
      setSavingPayout(false);
    }
  };

  const submitResolution = async () => {
    if (!resolution) { toast.error('Choose a resolution.'); return; }
    setSubmitting(true);
    try {
      const payload = { resolution };
      if (resolution === 'adjusted' || resolution === 'partially_approved' || resolution === 'approved') {
        if (adjRate !== '' && adjRate != null) payload.adjusted_rate = Number(adjRate);
        if (adjHours !== '' && adjHours != null) payload.adjusted_hours = Number(adjHours);
        if (adjAmount !== '' && adjAmount != null) payload.adjusted_amount = Number(adjAmount);
      }
      if (internalNote.trim()) payload.internal_note = internalNote.trim();
      if (talentResponse.trim()) payload.talent_visible_response = talentResponse.trim();
      const res = await api.post(`/admin/settlement-challenges/${active.id}/resolve`, payload);
      const msg = res.data?.message;
      if (resolution === 'request_info') {
        toast.success('Marked as awaiting more information — the talent has been notified.');
      } else if (res.data?.payout_ready === false) {
        toast.warning(typeof msg === 'string' ? msg : 'Resolved, but no payout was created — please follow up.');
      } else {
        toast.success(typeof msg === 'string' ? msg : 'Challenge resolved.');
      }
      setActive(null);
      load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resolve the challenge.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-settlement-challenges-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/admin/home')} className="text-cyan-600">
            <ArrowLeft className="w-4 h-4 mr-2" /> Admin Home
          </Button>
          <h1 className="text-lg font-bold text-gray-900">Settlement Challenges</h1>
          <div className="w-24" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 mb-4 items-center">
          {['open', 'resolved'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`challenges-tab-${t}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${tab === t ? 'bg-cyan-600 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}
            >
              {t} {t === 'open' && data.summary?.open ? `(${data.summary.open})` : ''}
            </button>
          ))}
          <button onClick={runAudit} data-testid="run-financial-audit-btn"
                  className="ml-auto px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 text-white hover:bg-amber-600">
            Financial audit
          </button>
        </div>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader><CardTitle className="text-gray-900 text-base">{tab === 'open' ? 'Open challenges' : 'Resolved challenges'}</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
            ) : data.challenges.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No {tab} challenges.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {data.challenges.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => openDetail(c)}
                    data-testid={`challenge-row-${c.id}`}
                    className="w-full text-left p-4 hover:bg-slate-50 transition"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-900">{c.talent_name} · {c.category_label}</p>
                        <p className="text-sm text-gray-500">{c.gig_name || c.role_name} • {c.date_worked ? formatDateFull(c.date_worked) : ''}</p>
                        <p className="text-[10px] font-mono text-gray-400 mt-0.5">
                          {c.case_number}
                          {c.source && c.source !== 'booking' && (
                            <span className={`ml-1 px-1.5 py-0.5 rounded-full not-italic ${
                              (c.source === 'evie_chat_recovery' || c.source === 'evie_failed_submission')
                                ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'}`}>
                              {c.source === 'evie_chat_recovery' ? 'reported via Evie'
                                : c.source === 'evie_failed_submission' ? 'via Evie · form failed'
                                : c.source === 'manual' ? 'manual entry' : 'no booking on file'}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{c.explanation}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-xs text-gray-400">Recorded</p>
                        <p className="font-medium text-gray-900">{fmt(c.original_amount)}</p>
                        {c.requested_amount != null && <p className="text-xs text-cyan-600">→ {fmt(c.requested_amount)} requested</p>}
                        {c.status !== 'under_review' && c.status !== 'pending' && (
                          <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{c.resolution || c.status}</span>
                        )}
                        {c.awaiting_info && (c.status === 'under_review' || c.status === 'pending') && (
                          <span className="inline-block mt-1 ml-1 text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700" data-testid={`awaiting-info-${c.id}`}>Awaiting info</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="challenge-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Review Settlement Challenge</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">{active?.talent_name} • {active?.gig_name || active?.role_name}</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-[11px] uppercase text-slate-400 font-semibold">Recorded</p>
                  <p className="text-sm text-slate-700 mt-1">{active.original_hours ?? '—'} hrs @ {active.original_rate != null ? `$${Number(active.original_rate).toFixed(2)}/hr` : '—'}</p>
                  <p className="text-lg font-bold text-slate-900">{fmt(active.original_amount)}</p>
                </div>
                <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3">
                  <p className="text-[11px] uppercase text-cyan-600 font-semibold">Talent requests</p>
                  <p className="text-sm text-slate-700 mt-1">{active.category_label}</p>
                  <p className="text-lg font-bold text-cyan-700">{active.requested_amount != null ? fmt(active.requested_amount) : '—'}</p>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-[11px] uppercase text-slate-400 font-semibold mb-1">Explanation</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{active.explanation}</p>
              </div>

              {active.manual_event && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="manual-event-detail">
                  <p className="text-[11px] uppercase text-amber-600 font-semibold mb-1">Manually-entered event (not on platform)</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-700">
                    {Object.entries({
                      'Event': active.manual_event.event_name,
                      'Date worked': active.manual_event.date_worked,
                      'City / State': [active.manual_event.city, active.manual_event.state].filter(Boolean).join(', '),
                      'Venue': active.manual_event.venue,
                      'Client / agency': active.manual_event.client_name,
                      'Start': active.manual_event.start_time,
                      'End': active.manual_event.end_time,
                      'Break (min)': active.manual_event.break_minutes,
                      'Onsite contact': active.manual_event.onsite_contact,
                    }).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}><span className="text-slate-400">{k}:</span> {v}</div>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(active.attachments) && active.attachments.length > 0 && (
                <div className="rounded-lg border border-slate-200 p-3" data-testid="challenge-attachments">
                  <p className="text-[11px] uppercase text-slate-400 font-semibold mb-1">Evidence</p>
                  <div className="flex flex-wrap gap-2">
                    {active.attachments.map((a, i) => (
                      <a key={i} href={a.url ? `${BACKEND}${a.url}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}` : '#'}
                         target="_blank" rel="noreferrer"
                         className="text-xs text-cyan-700 underline truncate max-w-[150px]"
                         data-testid={`attachment-${i}`}>
                        {a.name || `file ${i + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {(active.status === 'under_review' || active.status === 'pending') ? (
                <div className="space-y-3 border-t border-slate-100 pt-3">
                  <p className="text-sm font-medium text-slate-700">Resolution</p>
                  <div className="grid grid-cols-2 gap-2">
                    {RESOLUTIONS.map((r) => (
                      <label key={r.value} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${resolution === r.value ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200'}`} data-testid={`resolution-${r.value}`}>
                        <input type="radio" name="resolution" value={r.value} checked={resolution === r.value} onChange={() => setResolution(r.value)} className="accent-cyan-600" />
                        {r.label}
                      </label>
                    ))}
                  </div>

                  {(resolution === 'adjusted' || resolution === 'partially_approved' || resolution === 'approved') && (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-slate-600">Approved rate ($/hr)</label>
                        <Input type="number" step="0.01" value={adjRate} onChange={(e) => setAdjRate(e.target.value)} placeholder={active.original_rate != null ? `was $${active.original_rate}` : 'optional'} data-testid="adjust-rate" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600">Approved hours</label>
                        <Input type="number" step="0.25" value={adjHours} onChange={(e) => setAdjHours(e.target.value)} placeholder={active.original_hours != null ? `was ${active.original_hours}` : 'optional'} data-testid="adjust-hours" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-600">Approved amount ($)</label>
                        <Input type="number" step="0.01" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} placeholder={active.original_amount != null ? `was $${active.original_amount}` : 'optional'} data-testid="adjust-amount" />
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 -mt-1">Set a rate and/or hours to recompute earnings, or enter an exact approved amount. Approval updates the booking & settlement immediately; payment still issues on the normal Friday schedule.</p>

                  <div>
                    <label className="text-xs text-slate-600">Internal note (admin only)</label>
                    <Textarea rows={2} value={internalNote} onChange={(e) => setInternalNote(e.target.value)} data-testid="internal-note" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Response to talent (visible)</label>
                    <Textarea rows={2} value={talentResponse} onChange={(e) => setTalentResponse(e.target.value)} data-testid="talent-response" />
                  </div>
                </div>
              ) : (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                  <p className="text-slate-700"><span className="font-medium">Resolved:</span> {active.resolution || active.status}</p>
                  {active.talent_visible_response && <p className="text-slate-600 mt-1">Response: {active.talent_visible_response}</p>}
                </div>
              )}

              {(active.status === 'approved' || active.resolution === 'approved' ||
                active.status === 'partially_approved' || active.payout_update_requested ||
                active.revised_payout_date) && (
                <div className={`rounded-lg border p-3 space-y-2 ${active.payout_update_requested ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`} data-testid="payout-date-section">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] uppercase font-semibold text-emerald-700">Anticipated payout date</p>
                    {active.payout_update_requested && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-200 text-amber-800" data-testid="payout-followup-flag">Follow-up requested</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 space-y-0.5">
                    <p data-testid="live-queue-date">
                      Live payment queue: <span className="font-medium text-slate-800">{active.live_payout_date ? formatDateFull(active.live_payout_date) : '—'}</span>
                      {active.live_payout_overdue && <span className="ml-1 text-red-600">(date passed)</span>}
                    </p>
                    {active.revised_payout_date && (
                      <p data-testid="revised-date-current">Confirmed date on file: <span className="font-medium text-emerald-700">{formatDateFull(active.revised_payout_date)}</span></p>
                    )}
                  </div>
                  <div className="flex items-end gap-2 pt-1">
                    <div className="flex-1">
                      <label className="text-xs text-slate-600">Set updated payout date</label>
                      <Input type="date" value={payoutDate} onChange={(e) => setPayoutDate(e.target.value)} data-testid="payout-date-input" />
                    </div>
                    <Button onClick={savePayoutDate} disabled={savingPayout} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid="set-payout-date-btn">
                      {savingPayout ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set & notify talent'}
                    </Button>
                  </div>
                  <Input placeholder="Optional note (admin log)" value={payoutNote} onChange={(e) => setPayoutNote(e.target.value)} data-testid="payout-date-note" />
                  <p className="text-[11px] text-slate-400">Confirming a date updates the settlement record and notifies the talent through their enabled channels.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActive(null)}>Close</Button>
            {active && (active.status === 'under_review' || active.status === 'pending') && (
              <Button onClick={submitResolution} disabled={submitting} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="submit-resolution">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Resolve
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={auditOpen} onOpenChange={(o) => !o && setAuditOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle>Financial audit — approved challenges</DialogTitle>
            <DialogDescription>
              Approved challenges whose corrected rate/hours/amount was never applied to the
              booking & settlement. Reconcile applies the approved figure (idempotent — no double pay).
            </DialogDescription>
          </DialogHeader>
          {auditing ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
          ) : !audit ? null : (
            <div className="space-y-2">
              <p className="text-sm text-slate-600" data-testid="audit-summary">
                {audit.flagged_count} of {audit.approved_total} approved challenges need reconciliation.
              </p>
              {audit.flagged.length === 0 ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
                  All approved challenges are correctly reflected in bookings & settlements. ✓
                </div>
              ) : audit.flagged.map((f) => (
                <div key={f.challenge_id} className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3" data-testid={`audit-row-${f.challenge_id}`}>
                  <div className="text-sm min-w-0">
                    <p className="font-medium text-slate-800 truncate">{f.talent_name} <span className="font-mono text-[10px] text-slate-400">{f.case_number}</span></p>
                    <p className="text-xs text-slate-600">
                      {f.approved_rate != null && <>approved rate ${f.approved_rate}/hr · </>}
                      {f.approved_amount != null && <>approved ${f.approved_amount} · </>}
                      now showing {f.current_booking_amount != null ? `$${f.current_booking_amount}` : '—'}
                    </p>
                    <p className="text-[10px] text-amber-700">{(f.issues || []).map((i) => ISSUE_LABELS[i] || i).join(' · ')}</p>
                  </div>
                  <Button size="sm" onClick={() => reconcile(f.challenge_id)} disabled={reconciling === f.challenge_id}
                          className="bg-cyan-600 hover:bg-cyan-700 text-white shrink-0" data-testid={`reconcile-${f.challenge_id}`}>
                    {reconciling === f.challenge_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reconcile'}
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
